
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';
import { toast } from 'sonner';

export interface EnhancedLead {
  id: string;
  nursing_home_name: string;
  nursing_home_id: string;
  location?: string | null;
  status?: string | null;
  created_at: string;
  updated_at: string;
  user_id: string;
  notes?: string | null;
  follow_up_date?: string | null;
  is_public: boolean;
  bed_availability: number;
  fair_deal: boolean;
  price_range?: string | null;
  category?: string;
}

interface NursingHomeData {
  nh_slug: string;
  category?: string | null;
  price_range?: string | null;
}

interface WaitlistData {
  id: string;
  status: string;
  created_at: string;
  preferred_location: string[];
}

export function useDashboardData(user: User | null, refreshTrigger = 0) {
  const [nursingHomeLeads, setNursingHomeLeads] = useState<EnhancedLead[]>([]);
  const [waitlistData, setWaitlistData] = useState<WaitlistData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = async () => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      // Get all nursing home leads for the user with no filtering
      const { data: leadsData, error: leadsError } = await supabase
        .from('nursing_home_leads')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (leadsError) {
        console.error('Error fetching leads:', leadsError);
        throw leadsError;
      }

      // Log the actual fetched data for debugging
      console.log('Fetched nursing home leads data:', leadsData);
      
      if (!leadsData || leadsData.length === 0) {
        console.log('No leads data found for user:', user.id);
        setNursingHomeLeads([]);
        setIsLoading(false);
        return;
      }

      const nursingHomeIds = leadsData.map(lead => lead.nursing_home_id).filter(Boolean);
      console.log('Nursing home IDs to fetch:', nursingHomeIds);
      
      let nursingHomesData: NursingHomeData[] = [];
      if (nursingHomeIds.length > 0) {
        // Fetch nursing home details
        const { data: nhData, error: nhError } = await supabase
          .from('nursing_homes')
          .select('nh_slug, category, price_range')
          .in('nh_slug', nursingHomeIds);
          
        if (nhError) {
          console.error('Error fetching nursing homes:', nhError);
          // Continue with partial data rather than failing completely
        } else if (nhData) {
          nursingHomesData = nhData;
          console.log('Fetched nursing homes data:', nhData);
        }
      }
      
      const nursingHomesMap = new Map<string, NursingHomeData>();
      nursingHomesData.forEach(nh => {
        if (nh.nh_slug) {
          nursingHomesMap.set(nh.nh_slug, nh);
        }
      });
      
      // Process all leads without filtering out any
      const processedLeads = leadsData.map((lead) => {
        const nhData = lead.nursing_home_id ? nursingHomesMap.get(lead.nursing_home_id) : undefined;
        
        const enhancedLead: EnhancedLead = {
          ...lead,
          category: nhData?.category || 'Unknown',
          is_public: nhData?.category === 'Public',
          bed_availability: 0,
          fair_deal: true, // Default to true as most homes accept Fair Deal
          price_range: nhData?.price_range || null,
        };
        
        return enhancedLead;
      });
      
      console.log('Processed leads to display:', processedLeads);
      setNursingHomeLeads(processedLeads);
      
      // Fetch waitlist submission data
      const { data: waitlistData, error: waitlistError } = await supabase
        .from('waitlist_submissions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (waitlistError) {
        console.error('Error fetching waitlist submission:', waitlistError);
      } else if (waitlistData) {
        setWaitlistData(waitlistData);
        console.log('Fetched waitlist submission:', waitlistData);
      }
    } catch (error: any) {
      console.error('Error fetching dashboard data:', error);
      setError(error instanceof Error ? error : new Error('Unknown error fetching dashboard data'));
      toast.error("There was a problem loading your dashboard data", {
        description: "Please refresh the page to try again"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Refetch function that can be called from outside
  const refetch = () => {
    fetchData();
  };
  
  useEffect(() => {
    fetchData();
  }, [user, refreshTrigger]);
  
  return { nursingHomeLeads, waitlistData, isLoading, error, refetch };
}
